<template>
  <div>
    <FormHeader bookmarkable>
      Two-Factor Authentication

      <template #sub-heading>
        <template v-if="'phone' == otpType"
          >Enter the code from your authenticator app</template
        >
        <template v-else
          >Enter the six-digit code that was sent to your email
          address.</template
        >
      </template>
    </FormHeader>

    <div class="mt-4 d-flex align-items-center justify-content-center">
      <InputCode v-model="otpValue" @input="handleVerification" />
      <base-button
        v-if="isEmailOTP"
        @click="handleResendEmailOTP"
        id="resend-email-otp--button"
        class="p-2 ml-3"
        native-type="button"
        type="primary"
        round
      >
        <i
          :class="{
            'bi-envelope-check': emailOTPSent,
            'bi-envelope-at': !emailOTPSent
          }"
          class="mx-0"
        ></i>
      </base-button>
    </div>

    <div class="mt-3 font-weight-bold text-center small">
      <template v-if="deviceEnabled">
        <div v-if="isEmailOTP">
          Use authenticator phone app by clicking here:
        </div>
        <div v-else>
          Use email to receive authorization code by clicking here:
        </div>

        <base-button
          @click="() => (otpType = isEmailOTP ? 'phone' : 'email')"
          type="primary"
          size="lg"
          class="mt-3 mb-4"
        >
          <template v-if="isEmailOTP">Use Authenticator App</template>
          <template v-else>Use Email Authorization</template>
        </base-button>
      </template>

      <div>
        Receive a one-time
        <router-link :to="{ name: 'Home' }" class="text-dark text-underline"
          >magic link</router-link
        >
        to login without a password.
      </div>
    </div>
  </div>
</template>

<script>
import 'bootstrap-icons/font/bootstrap-icons.css'

import Vue from 'vue'
import { defineComponent, computed, ref, watch } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'

import { AuthenticationService, OTPService } from '@services'

import FormHeader from '@components/FormHeader'
import InputCode from '@components/InputCode.vue'

export default defineComponent({
  name: 'TwoFactorAuthentication',
  components: {
    FormHeader,
    InputCode
  },
  props: {
    email: {
      type: String,
      required: true
    },
    password: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'email'
    },
    deviceEnabled: Boolean
  },
  setup(props) {
    const { useActions } = createVuexHelpers()
    const {
      setAccessToken,
      setRefreshToken,

      setUser
    } = useActions(['setAccessToken', 'setRefreshToken', 'setUser'])

    const otpType = ref(props.type)
    const otpValue = ref('')
    const emailOTPSent = ref('phone' == props.type ? false : true)

    const isEmailOTP = computed(() => 'email' === otpType.value)

    const handleResendEmailOTP = async () => {
      await AuthenticationService.login(props.email, props.password, true)

      emailOTPSent.value = true

      Vue.prototype.$toasted.show('Login OTP sent successfully')
    }

    const handleVerification = async (token) => {
      const type = otpType.value

      const res = await OTPService.verify(
        props.email,
        props.password,
        token,
        type
      ).catch((err) => {
        otpValue.value = ''

        Vue.prototype.$toasted.show(err.response.data.data[0], {
          type: 'error'
        })
      })

      if ('undefined' == typeof res) return

      const { access_token, refresh_token, user } = res.data.data
      user.device_2fa_enabled = 'phone' === props.type

      Vue.prototype.$toasted.show('Logged in successfully')

      setAccessToken(access_token)
      setRefreshToken(refresh_token)

      setUser(user)
    }

    watch(
      () => otpType.value,
      (newOtpType) => {
        if ('phone' == newOtpType) return

        handleResendEmailOTP()
      }
    )

    return {
      otpType,
      otpValue,
      emailOTPSent,
      isEmailOTP,
      handleResendEmailOTP,
      handleVerification
    }
  }
})
</script>

<style scoped>
#resend-email-otp--button {
  min-width: 47px;
  height: 47px;
}
</style>
